import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useLangPrefix, withLangPrefix } from '../hooks/useLangPrefix';
import { VideoTestimonials } from '../components/VideoTestimonials';
import { QualiopiBadge } from '../components/QualiopiBadge';

const HERO_VIDEO_ID = "9SShTEM03_Q";

// Rotating photos for the "Artisan STS près de chez vous" home CTA.
// Served from the frontend `public/` folder so they're guaranteed to be
// available in production (backend uploads/ are not bundled with deploys).
const ARTISAN_ROTATING_IMAGES = [
  "/formations/cover-multisupport.png",
  "/realisations/nico-machine.jpg",
  "/realisations/atelier-1.jpg",
  "/realisations/atelier-2.jpg",
  "/realisations/atelier-3.jpg",
  "/realisations/atelier-4.jpg",
  "/realisations/atelier-5.jpg",
];

const PERSONAS = [
  { img: "https://images.unsplash.com/photo-1573497019418-b400bb3ab074?w=800&q=70&auto=format&fit=crop", titleKey: "home.persona1_title", descKey: "home.persona1_desc", tagKey: "home.persona1_tag" },
  { img: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&q=70&auto=format&fit=crop", titleKey: "home.persona2_title", descKey: "home.persona2_desc", tagKey: "home.persona2_tag" },
  { img: "https://images.unsplash.com/photo-1556745753-b2904692b3cd?w=800&q=70&auto=format&fit=crop", titleKey: "home.persona3_title", descKey: "home.persona3_desc", tagKey: "home.persona3_tag" },
  { img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&q=70&auto=format&fit=crop", titleKey: "home.persona4_title", descKey: "home.persona4_desc", tagKey: "home.persona4_tag" }
];

const OUTCOMES = [
  { num: "100%", labelKey: "home.outcomes_stat_success", noteKey: "home.outcomes_since" },
  { numKey: "home.outcomes_stat_age_num", labelKey: "home.outcomes_stat_age", noteKey: "home.outcomes_since" },
  { num: "55%", labelKey: "home.outcomes_stat_women", noteKey: "home.outcomes_since" },
  { num: "45%", labelKey: "home.outcomes_stat_men", noteKey: "home.outcomes_since" }
];

// Photos téléchargées du site de référence sellerie-academie.fr — hébergées en local
const SA = "/sa";

const FORMATION_IMAGES = {
  decouverte: `${SA}/fabrication-dun-coussin.jpg`,
  complete:   `${SA}/nico-machine-a-coudre-scaled-1.jpg`,
  auto:       `${SA}/IMG_20220428_194010238-scaled-1.jpg`,
  moto:       `${SA}/IMG_20220401_105744457-scaled-1.jpg`,
  nautique:   `${SA}/img014.jpg`,
  habitat:    `${SA}/38a18be2087b9b5e14b06781c43f0250.jpg`,
  dessin:     `${SA}/IMG_20220330_182110015-scaled-1.jpg`
};

// Material/process tags shown as authentic "savoir-faire" markers on each
// formation card. Source: sellerie-academie.fr (sellerie-garnissage, pas que cuir).
const FORMATION_MATERIALS = {
  decouverte: ["GARNISSAGE", "PATRON", "MOUSSE"],
  complete:   ["GARNISSAGE", "DRAPERIE", "CAPITONNAGE", "FINITION"],
  auto:       ["INTÉRIEUR AUTO", "GARNISSAGE", "FINITION"],
  moto:       ["SELLE", "GARNISSAGE", "ASSEMBLAGE"],
  nautique:   ["BANQUETTE", "MILIEU MARIN", "ASSEMBLAGE"],
  habitat:    ["FAUTEUIL", "CAPITONNAGE", "REVÊTEMENT"],
  dessin:     ["PATRON", "GABARIT", "MESURE"]
};

// Tactile process strip — large close-up shots of the school's craft for
// the "Matière" band. Source: photos officielles sellerie-academie.fr.
// Focus on geste & matière (pas marque de machine, pas que cuir).
const MATIERE_IMAGES = [
  // GARNISSAGE — fabrication d'un coussin (photo officielle Sellerie Académie)
  { src: `${SA}/fabrication-dun-coussin.jpg`, labelKey: "home.matiere1_label", subKey: "home.matiere1_sub" },
  // COUTURE — Nico à la machine triple entraînement
  { src: `${SA}/nico-machine-a-coudre-scaled-1.jpg`, labelKey: "home.matiere2_label", subKey: "home.matiere2_sub" },
  // ATELIER — vue d'atelier
  { src: `${SA}/IMG_20220330_181615888-scaled-1.jpg`, labelKey: "home.matiere3_label", subKey: "home.matiere3_sub" },
  // ASSEMBLAGE — gros plan dessin technique / assemblage
  { src: `${SA}/IMG_20220330_182110015-scaled-1.jpg`, labelKey: "home.matiere4_label", subKey: "home.matiere4_sub" }
];

const FORMATION_PROMISES = {
  decouverte: "Valide ton intérêt pour le métier en 3 jours",
  complete: "Deviens sellier polyvalent en 23 jours",
  auto: "Restaure des intérieurs auto haut de gamme",
  moto: "Crée des selles sur-mesure professionnelles",
  nautique: "Maîtrise la sellerie de bateaux",
  habitat: "Réalise fauteuils, banquettes & sellerie médicale",
  dessin: "Trace tes propres patrons et gabarits",
  cover: "Donnez une nouvelle vie, avec un nouveau style !"
};

const TESTIMONIALS = [
  {
    quote: "À 38 ans, j'ai quitté 15 ans de commercial. Benki m'a appris en 23 jours ce que j'avais essayé de comprendre seul pendant 6 mois. Mon atelier ouvre à Aix.",
    author: "Mathieu R.",
    role: "Reconversion · Sellerie auto"
  },
  {
    quote: "Une pédagogie incroyable, sans jugement. La main passe vraiment de Benki à nous. Je me lance désormais en auto-entreprise.",
    author: "Sophie L.",
    role: "Sellerie habitat"
  },
  {
    quote: "Trois semaines intenses. Premier client une semaine après la fin de la formation. France Travail a financé sans difficulté grâce à Qualiopi.",
    author: "Karim B.",
    role: "Sellerie moto"
  }
];

export const HomePage = () => {
  const { t } = useTranslation();
  const { lang } = useLangPrefix();
  const [courses, setCourses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [teaserEmail, setTeaserEmail] = useState('');
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    Promise.all([
      api.getCourses({ course_type: 'onsite' }),
      api.getSessions()
    ]).then(([c, s]) => {
      setCourses(c);
      setSessions(s);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const i = setInterval(() => setActiveTestimonial((p) => (p + 1) % TESTIMONIALS.length), 7000);
    return () => clearInterval(i);
  }, []);

  const getNextSession = (courseId) => {
    const now = new Date();
    return sessions
      .filter(s => s.course_id === courseId && parseISO(s.start_date) > now)
      .sort((a, b) => parseISO(a.start_date) - parseISO(b.start_date))[0];
  };

  const handleTeaser = (e) => {
    e.preventDefault();
    if (!teaserEmail) return;
    toast.success('Vous serez les premiers informés du lancement.');
    setTeaserEmail('');
  };

  return (
    <div className="bg-[#FAF7F0] text-[#1F2A4A]">
      {/* ───────── 01 · HERO ───────── */}
      <section className="relative h-screen w-full flex items-end">
        {/* Video background */}
        <div className="absolute inset-0 overflow-hidden">
          <iframe
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
            style={{ width: '177.78vh', height: '100vh', minWidth: '100vw', minHeight: '56.25vw' }}
            src={`https://www.youtube.com/embed/${HERO_VIDEO_ID}?autoplay=1&mute=1&loop=1&playlist=${HERO_VIDEO_ID}&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1`}
            title="Sellerie Académie"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          />
          <div className="absolute inset-0 bg-[#172239]/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#172239] via-[#172239]/30 to-[#172239]/60" />
          <div className="absolute inset-0 opacity-[0.04] pointer-events-none mix-blend-overlay" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-12 md:pb-16">
          <p className="text-sm uppercase tracking-[0.35em] text-[#C9B896] mb-6">Sellerie Académie · Istres · Provence</p>

          <h1 className="font-light text-white leading-[0.95] tracking-tight max-w-5xl" data-testid="hero-title">
            <span className="block text-4xl md:text-6xl lg:text-7xl">{t('home.hero_line1')}</span>
            <span className="block font-serif italic font-medium text-[#C9B896] text-5xl md:text-7xl lg:text-8xl mt-2">{t('home.hero_line2')}</span>
          </h1>

          <p className="font-light text-white/75 text-lg md:text-xl max-w-xl mt-10 leading-relaxed" data-testid="hero-description">
            {t('home.hero_subtitle')}
          </p>

          <div className="mt-12 flex items-baseline gap-8">
            <Link to={withLangPrefix('/formations-sur-place', lang)} className="group inline-flex items-baseline gap-3 text-white border-b border-[#C9B896] pb-1.5 hover:gap-5 transition-all duration-500" data-testid="hero-cta">
              <span className="text-sm uppercase tracking-[0.25em]">{t('home.cta_discover')}</span>
              <span className="text-[#C9B896]">→</span>
            </Link>
          </div>
        </div>

        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3 opacity-70">
          <span className="text-[10px] font-mono tracking-[0.4em] text-white/70">SCROLL</span>
          <span className="block w-px h-12 bg-gradient-to-b from-transparent via-white/30 to-[#C9B896]" />
        </div>
      </section>

      {/* ───────── 02 · L'ÉCOLE — Manifeste ───────── */}
      <section className="py-14 md:py-20 relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-4">
              <h2 className="font-light text-2xl md:text-4xl leading-tight text-[#1F2A4A]">
                {t('home.manifest_title_l1')} <br />
                <span className="font-serif italic">{t('home.manifest_title_em')}</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7 md:col-start-6 space-y-6 text-[#4A5568] text-lg leading-relaxed font-light">
              <p>{t('home.manifest_p1')}</p>
              <p>{t('home.manifest_p2')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Separator */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <svg width="100%" height="2" viewBox="0 0 1200 2" preserveAspectRatio="none">
          <line x1="0" y1="1" x2="1200" y2="1" stroke="#C9B896" strokeWidth="1" strokeDasharray="4 4" />
        </svg>
      </div>

      {/* ───────── 03 · À QUI S'ADRESSE ───────── */}
      <section className="py-12 md:py-16 bg-[#FAF7F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 gap-6 mb-6 md:mb-10">
            <div className="col-span-12 md:col-span-6">
              <h2 className="font-light text-2xl md:text-4xl lg:text-5xl leading-[1.05] text-[#1F2A4A]">
                {t('home.persona_title_l1')}<br />
                <span className="font-serif italic">{t('home.persona_title_em')}</span><br />
                <span>{t('home.persona_title_l3')}</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:col-start-8 md:self-end">
              <p className="text-[#4A5568] text-lg leading-relaxed font-light">
                {t('home.persona_subtitle')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {PERSONAS.map((p, i) => {
              const title = t(p.titleKey);
              const words = title.split(' ');
              return (
                <Link
                  to={withLangPrefix('/formations-sur-place', lang)}
                  key={i}
                  className="group relative overflow-hidden rounded-2xl bg-[#1F2A4A] aspect-[4/3] md:aspect-[5/4] cursor-pointer block hover:shadow-2xl hover:shadow-[#1F2A4A]/30 transition-shadow duration-500"
                  data-testid={`persona-${i}`}
                >
                  <img
                    src={p.img}
                    alt={title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-105 opacity-60 group-hover:opacity-80"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1F2A4A] via-[#1F2A4A]/50 to-transparent" />
                  <div className="relative h-full flex flex-col justify-end p-8 md:p-10">
                    <span className="inline-flex items-center self-start px-3 py-1 rounded-full bg-[#C9B896]/95 text-[#1F2A4A] text-[10px] font-semibold uppercase tracking-[0.2em] mb-5">
                      {t(p.tagKey)}
                    </span>
                    <h3 className="font-light text-white text-2xl md:text-3xl lg:text-4xl mb-3 leading-tight">
                      {words.map((w, idx) => idx >= words.length - 2 ? <span key={idx} className="font-serif italic text-[#C9B896]">{w} </span> : w + ' ')}
                    </h3>
                    <p className="text-white/80 text-sm md:text-base leading-relaxed font-light max-w-md mb-4">
                      {t(p.descKey)}
                    </p>
                    <span className="inline-flex items-center gap-2 text-[#C9B896] text-xs uppercase tracking-[0.25em] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {t('home.persona_card_cta')} <span aria-hidden>→</span>
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="mt-14 text-center">
            <Link to={withLangPrefix('/formations-sur-place', lang)} className="group inline-flex items-baseline gap-3 text-[#1F2A4A] border-b border-[#C9B896] pb-1 hover:gap-5 transition-all duration-500">
              <span className="text-sm uppercase tracking-[0.25em]">{t('home.persona_cta')}</span>
              <span className="text-[#C9B896]">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Separator */}
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <svg width="100%" height="2" viewBox="0 0 1200 2" preserveAspectRatio="none">
          <line x1="0" y1="1" x2="1200" y2="1" stroke="#C9B896" strokeWidth="1" strokeDasharray="4 4" />
        </svg>
      </div>

      {/* ───────── 03b · MATIÈRE — tactile process strip ───────── */}
      <section className="bg-[#1F2A4A] text-[#FAF7F0] py-10 md:py-14 relative overflow-hidden" data-testid="matiere-section">
        {/* Grain overlay */}
        <div className="absolute inset-0 opacity-[0.07] pointer-events-none mix-blend-overlay" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="grid grid-cols-12 mb-6 md:mb-10 items-end">
            <div className="col-span-12 md:col-span-7">
              <h2 className="font-light text-3xl md:text-5xl leading-[1.05] tracking-tight text-white">
                {t('home.matiere_title_l1')}<br />
                <span className="font-serif italic text-[#C9B896]">{t('home.matiere_title_em')}</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-4 md:col-start-9 mt-8 md:mt-0">
              <p className="text-white/65 font-light leading-relaxed">
                {t('home.matiere_subtitle')}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5" data-testid="matiere-grid">
            {MATIERE_IMAGES.map((m, i) => (
              <Link
                to={withLangPrefix('/formations-sur-place', lang)}
                key={m.labelKey}
                className="group relative overflow-hidden bg-black/40 aspect-[4/5] block rounded-lg hover:shadow-2xl hover:shadow-[#C9B896]/20 transition-shadow duration-500"
                data-testid={`matiere-card-${i}`}
              >
                <img
                  src={m.src}
                  alt={t(m.labelKey)}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                  style={{ filter: "saturate(0.85) contrast(1.05)" }}
                />
                {/* Sepia/navy wash */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#1F2A4A] via-[#1F2A4A]/40 to-transparent" />
                <div className="absolute inset-0 bg-[#C9B896]/0 group-hover:bg-[#C9B896]/15 transition-colors duration-700" />
                {/* Index */}
                <span className="absolute top-4 left-4 text-[10px] font-mono tracking-[0.25em] text-[#C9B896]">
                  0{i + 1}
                </span>
                <figcaption className="absolute bottom-5 left-5 right-5">
                  <p className="font-serif italic text-[#C9B896] text-sm mb-1 opacity-80">{t(m.subKey)}</p>
                  <p className="text-white text-2xl md:text-3xl font-light tracking-wide">{t(m.labelKey)}</p>
                </figcaption>
                {/* Dashed stitching border on hover */}
                <span className="absolute inset-[8px] border border-dashed border-[#C9B896]/0 group-hover:border-[#C9B896]/55 transition-colors duration-700 pointer-events-none" />
                {/* CTA arrow */}
                <span className="absolute top-4 right-4 w-8 h-8 rounded-full bg-[#C9B896]/0 group-hover:bg-[#C9B896] text-[#C9B896] group-hover:text-[#1F2A4A] flex items-center justify-center transition-all duration-500" aria-hidden>→</span>
              </Link>
            ))}
          </div>

          {/* Explore CTA */}
          <div className="mt-12 text-center">
            <Link to={withLangPrefix('/formations-sur-place', lang)} className="group inline-flex items-baseline gap-3 text-white border-b border-[#C9B896] pb-1 hover:gap-5 transition-all duration-500" data-testid="matiere-explore-cta">
              <span className="text-sm uppercase tracking-[0.25em]">{t('home.matiere_explore')}</span>
              <span className="text-[#C9B896]">→</span>
            </Link>
          </div>

          {/* Savoir-faire line (no leather monopoly — sellerie-garnissage covers many materials) */}
          <div className="mt-6 md:mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs md:text-[13px] font-mono tracking-[0.35em] text-white font-medium">
            <span>SELLERIE</span><span className="text-[#C9B896]">·</span>
            <span>GARNISSAGE</span><span className="text-[#C9B896]">·</span>
            <span>CAPITONNAGE</span><span className="text-[#C9B896]">·</span>
            <span>DRAPERIE</span><span className="text-[#C9B896]">·</span>
            <span>COUTURE</span><span className="text-[#C9B896]">·</span>
            <span>PATRON</span><span className="text-[#C9B896]">·</span>
            <span>FINITION</span>
          </div>
        </div>
      </section>

      {/* ───────── 03c · CHIFFRES — preuve sociale authentique ───────── */}
      <section className="bg-[#FAF7F0] py-10 md:py-14 border-y border-[#1F2A4A]/8 relative" data-testid="proof-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-12 md:gap-y-0 md:divide-x md:divide-[#1F2A4A]/12">
            {[
              { value: "100", suffix: "%", label: "de réussite", caption: "à l'issue des sessions" },
              { value: "22", suffix: "+", label: "élèves déjà formés", caption: "" },
              { value: "07", suffix: "", label: "programmes", caption: "de 3 à 23 jours" },
              { value: "01", suffix: "", label: "atelier-école", caption: "à Istres · Provence" },
            ].map((stat, i) => (
              <div key={i} className="md:px-10 first:md:pl-0 last:md:pr-0 text-center md:text-left" data-testid={`proof-${i}`}>
                <div className="flex items-baseline gap-1 justify-center md:justify-start mb-3">
                  <span className="font-serif italic font-light text-[#1F2A4A] text-6xl md:text-7xl leading-none">
                    {stat.value}
                  </span>
                  <span className="font-serif italic text-[#C9B896] text-3xl md:text-4xl leading-none">
                    {stat.suffix}
                  </span>
                </div>
                <p className="text-[#1F2A4A] text-sm md:text-base font-light mb-1">{stat.label}</p>
                {stat.caption && (
                  <p className="text-[10px] md:text-xs font-mono uppercase tracking-[0.25em] text-[#C9B896]">
                    {stat.caption}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Qualiopi badge line */}
          <div className="mt-8 md:mt-10 pt-8 border-t border-dashed border-[#1F2A4A]/15 flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 text-xs md:text-[13px] font-mono tracking-[0.28em] uppercase text-[#1F2A4A] font-medium">
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C9B896]" />
              Certifié Qualiopi
            </span>
            <span className="hidden md:inline text-[#C9B896]">·</span>
            <span className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C9B896]" />
              Plusieurs financements possibles
            </span>
          </div>
        </div>
      </section>

      {/* ───────── 04 · FORMATIONS — editorial grid with full-bleed imagery ───────── */}
      <section className="py-14 md:py-20 relative bg-[#FAF7F0]" data-testid="formations-section">
        {/* Paper grain */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="grid grid-cols-12 mb-6 md:mb-10">
            <div className="col-span-12 md:col-span-7">
              <h2 className="font-light tracking-tight text-[#1F2A4A] text-4xl md:text-6xl leading-[0.95]">
                Sellerie automobile, moto,<br />
                <span className="font-serif italic text-[#C9B896]">nautique &amp; habitat.</span>
              </h2>
              <p className="text-sm md:text-base text-[#4A5568] mt-6 max-w-xl leading-relaxed">
                Toutes nos formations sont des cursus <strong>généralistes de la sellerie</strong> — le savoir-faire
                acquis te permet ensuite de travailler le <strong>cuir auto</strong>, la <strong>selle moto</strong>,
                le <strong>bateau</strong>, le <strong>mobilier d'habitat</strong> ou la sellerie <strong>médicale</strong>.
              </p>

              {/* Specialties tag cloud — SEO + visual cue */}
              <div className="flex flex-wrap gap-2 mt-6" data-testid="formations-specialities">
                {[
                  'Sellerie automobile', 'Sellerie moto', 'Sellerie nautique',
                  'Sellerie habitat', 'Sellerie médicale', 'Capitonnage',
                  'Garnissage', 'Coutures sellier', 'Bâche & couverture',
                ].map((s) => (
                  <span key={s} className="text-[11px] font-mono uppercase tracking-[0.15em] text-[#1F2A4A]/65 bg-white/60 border border-[#1F2A4A]/10 rounded-full px-3 py-1">
                    {s}
                  </span>
                ))}
              </div>
            </div>
            <div className="col-span-12 md:col-span-4 md:col-start-9 mt-8 md:mt-0 md:self-end">
              <p className="text-[#4A5568] text-base leading-relaxed font-light">
                De l'initiation de 3 jours à la formation complète de 23 jours,
                en présentiel à Istres. <strong>Plusieurs solutions de financement disponibles.</strong>
                Une certification artisanale unique en France pour ouvrir ton atelier.
              </p>
            </div>
          </div>

          {/* Editorial grid: alternating image-left / image-right rows */}
          <div className="space-y-20 md:space-y-32" data-testid="formations-list">
            {courses.map((course, idx) => {
              const next = getNextSession(course.id);
              const spots = next ? next.available_spots - next.enrolled_count : 0;
              const img = course.thumbnail_url || FORMATION_IMAGES[course.category] || FORMATION_IMAGES.complete;
              const promise = FORMATION_PROMISES[course.category] || '';
              const materials = FORMATION_MATERIALS[course.category] || [];
              const reversed = idx % 2 === 1;

              return (
                <article
                  key={course.id}
                  className="group grid grid-cols-12 gap-6 md:gap-10 items-center"
                  data-testid="formation-row"
                >
                  {/* Image (large) */}
                  <Link
                    to={withLangPrefix(`/formation/${course.id}`, lang)}
                    className={`col-span-12 md:col-span-7 relative aspect-[4/3] md:aspect-[3/2] overflow-hidden bg-[#1F2A4A] ${reversed ? 'md:order-2' : ''}`}
                  >
                    <img
                      src={img}
                      alt={course.title}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105"
                      style={{ filter: "saturate(0.92) contrast(1.05)" }}
                    />
                    {/* Warm overlay */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#1F2A4A]/35 via-transparent to-[#C9B896]/10" />
                    {/* Huge numeral */}
                    <span className="absolute top-6 left-6 md:top-10 md:left-10 font-serif italic text-white/90 leading-none text-[120px] md:text-[180px] drop-shadow-[0_4px_24px_rgba(0,0,0,0.45)]">
                      {String(idx + 1).padStart(2, '0')}
                    </span>
                    {/* Dashed stitching frame on hover */}
                    <span className="absolute inset-[14px] border border-dashed border-[#C9B896]/0 group-hover:border-[#C9B896]/75 transition-colors duration-700 pointer-events-none" />
                  </Link>

                  {/* Text */}
                  <div className={`col-span-12 md:col-span-5 ${reversed ? 'md:order-1 md:pr-4' : 'md:pl-4'}`}>
                    <p className="text-[10px] font-mono uppercase tracking-[0.35em] text-[#C9B896] mb-3">
                      {course.category === 'decouverte' && 'Découverte · 3 jours'}
                      {course.category === 'complete' && 'Formation complète · 23 jours'}
                      {course.category === 'auto' && 'Sellerie automobile · 23 jours'}
                      {course.category === 'moto' && 'Sellerie moto · 23 jours'}
                      {course.category === 'nautique' && 'Sellerie nautique · 23 jours'}
                      {course.category === 'habitat' && 'Habitat & médical · 23 jours'}
                      {course.category === 'dessin' && 'Dessin technique · 2 jours'}
                    </p>
                    <h3 className="font-light text-2xl md:text-3xl lg:text-4xl text-[#1F2A4A] leading-[1.05] tracking-tight mb-5 group-hover:text-[#C9B896] transition-colors duration-500">
                      {course.title}
                    </h3>
                    {promise && (
                      <p className="font-serif italic text-lg md:text-xl text-[#4A5568] mb-6 leading-snug">
                        « {promise} »
                      </p>
                    )}
                    {/* Material tags */}
                    {materials.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-7">
                        {materials.map((m) => (
                          <span
                            key={m}
                            className="text-[10px] font-mono tracking-[0.25em] uppercase text-[#1F2A4A]/70 border border-[#1F2A4A]/15 px-3 py-1 rounded-full bg-white/40 backdrop-blur-sm"
                          >
                            {m}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Session info + CTA */}
                    <div className="flex flex-wrap items-baseline gap-6 pt-5 border-t border-[#1F2A4A]/12">
                      {next ? (
                        <div>
                          <span className="block text-[10px] uppercase tracking-[0.3em] text-[#C9B896] mb-1">Prochaine session</span>
                          <span className="text-sm text-[#1F2A4A] font-light">
                            {format(parseISO(next.start_date), 'd MMMM yyyy', { locale: fr })}
                            {spots > 0 && <span className="text-green-700 ml-3">· {spots} places</span>}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-[#4A5568] italic">Prochaine session à venir</span>
                      )}
                      <Link
                        to={withLangPrefix(`/formation/${course.id}`, lang)}
                        className="ml-auto inline-flex items-baseline gap-2 text-sm uppercase tracking-[0.25em] font-medium text-[#1F2A4A] border-b border-[#C9B896] pb-1 hover:gap-4 transition-all duration-500"
                      >
                        Découvrir <span className="text-[#C9B896]">→</span>
                      </Link>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          <div className="mt-10 md:mt-14 text-center">
            <Link to={withLangPrefix('/formations-sur-place', lang)} className="inline-flex items-baseline gap-3 text-[#1F2A4A] border-b-2 border-[#C9B896] pb-1.5 hover:gap-5 transition-all duration-500 text-sm uppercase tracking-[0.3em] font-medium">
              Toutes les sessions ouvertes <span className="text-[#C9B896] text-lg">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* ───────── 05 · APRÈS LA FORMATION — Outcomes / Transformation ───────── */}
      <section className="py-12 md:py-16 bg-[#1F2A4A] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none mix-blend-overlay" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-15 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 80% 50%, #C9B896 0%, transparent 60%)' }} />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 gap-6 mb-8 md:mb-10">
            <div className="col-span-12 md:col-span-7">
              <h2 className="font-light text-2xl md:text-4xl lg:text-5xl leading-[1.05] text-white">
                {t('home.outcomes_title_l1')}<br />
                <span className="font-serif italic text-[#C9B896]">{t('home.outcomes_title_em')}</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-4 md:col-start-9 md:self-end">
              <p className="text-white/70 text-base leading-relaxed font-light">
                {t('home.outcomes_subtitle')}
              </p>
            </div>
          </div>

          {/* Outcome stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden" data-testid="outcomes-stats">
            {OUTCOMES.map((o, i) => (
              <div key={i} className="bg-[#1F2A4A] p-8 md:p-12 hover:bg-[#243463] transition-colors duration-500">
                <p className="text-4xl md:text-6xl font-light text-[#C9B896] leading-none">{o.numKey ? t(o.numKey) : o.num}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-white/75 mt-4">{t(o.labelKey)}</p>
                {o.noteKey && (
                  <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#C9B896]/70 mt-2">
                    · {t(o.noteKey)}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Qualiopi disclaimer — required for transparency on stats methodology */}
          <p className="text-[11px] text-white/45 italic mt-5 max-w-3xl leading-relaxed">
            {t('home.outcomes_qualiopi')}
          </p>

          {/* What you get */}
          <div className="grid md:grid-cols-3 gap-6 mt-6 md:mt-10">
            {[
              { title: t('home.outcomes_block1_title'), desc: t('home.outcomes_block1_desc') },
              { title: t('home.outcomes_block2_title'), desc: t('home.outcomes_block2_desc') },
              { title: t('home.outcomes_block3_title'), desc: t('home.outcomes_block3_desc') }
            ].map((item, i) => (
              <div key={i} className="border-t border-[#C9B896]/30 pt-6">
                <span className="text-xs font-mono text-[#C9B896] tracking-wider">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="font-light text-xl md:text-2xl text-white mt-2 mb-3 leading-tight">{item.title}</h3>
                <p className="text-white/65 text-sm leading-relaxed font-light">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-6">
            <Link to={withLangPrefix('/formations-sur-place', lang)}>
              <Button className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] rounded-full px-9 py-7 text-sm uppercase tracking-[0.25em] font-medium shadow-2xl shadow-[#C9B896]/30" data-testid="outcomes-cta">
                {t('home.outcomes_cta')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ───────── 05b · TÉMOIGNAGES VIDÉO (5 vidéos YouTube en carousel) ───────── */}
      <VideoTestimonials />

      {/* ───────── 05c · DEMANDE D'ARTISAN STS PRÈS DE CHEZ MOI (public lead capture) ───────── */}
      <ArtisanNetworkCTA lang={lang} />

      {/* ───────── 06 · TÉMOIGNAGES TEXTUELS — hidden at client request.
          Only the video testimonials section (05b) remains. Kept commented
          for easy reactivation later. ───────── */}
      {false && (
      <section className="py-14 md:py-20 bg-[#F4EFE3] relative overflow-hidden" data-testid="testimonials-section">
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />

        <div className="relative max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif italic font-medium text-2xl md:text-4xl lg:text-5xl text-[#1F2A4A] leading-[1.1] max-w-3xl mx-auto">
              Qui mieux que vous<br />pour en parler&nbsp;?
            </h2>
          </div>

          {/* Single rotating quote */}
          <div className="relative min-h-[280px] md:min-h-[240px]">
            {TESTIMONIALS.map((t, i) => (
              <blockquote
                key={i}
                className={`absolute inset-0 transition-all duration-1000 flex flex-col items-center text-center ${
                  i === activeTestimonial ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
                }`}
                data-testid={`testimonial-quote-${i}`}
              >
                <span className="font-serif italic text-6xl md:text-8xl text-[#C9B896]/40 leading-none mb-4">"</span>
                <p className="font-serif italic font-normal text-2xl md:text-3xl lg:text-4xl text-[#1F2A4A] leading-relaxed max-w-3xl mb-8">
                  {t.quote}
                </p>
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, idx) => (
                    <svg key={idx} className="w-3.5 h-3.5 fill-[#C9B896]" viewBox="0 0 24 24"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                  ))}
                </div>
                <p className="text-sm text-[#1F2A4A] font-medium">{t.author}</p>
                <p className="text-xs uppercase tracking-[0.25em] text-[#4A5568]/70 mt-1">{t.role}</p>
              </blockquote>
            ))}
          </div>

          {/* Dots */}
          <div className="flex items-center justify-center gap-2 mt-10">
            {TESTIMONIALS.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveTestimonial(i)}
                aria-label={`Témoignage ${i + 1}`}
                data-testid={`testimonial-dot-${i}`}
                className={`h-px transition-all duration-500 ${
                  i === activeTestimonial ? 'w-12 bg-[#1F2A4A]' : 'w-6 bg-[#1F2A4A]/25 hover:bg-[#1F2A4A]/50'
                }`}
              />
            ))}
          </div>

          <p className="text-center text-xs uppercase tracking-[0.3em] text-[#4A5568]/70 mt-12">
            5/5 · 22 témoignages d'élèves déjà formés
          </p>
        </div>
      </section>
      )}

      {/* ───────── 07 · L'ARTISAN-FORMATEUR (compact) ───────── */}
      <section className="py-10 md:py-14 bg-[#FAF7F0]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
            <div className="relative shrink-0">
              <svg className="absolute inset-0 w-full h-full -m-1.5 pointer-events-none" viewBox="0 0 100 100" fill="none">
                <circle cx="50" cy="50" r="49" stroke="#C9B896" strokeWidth="0.4" strokeDasharray="1.5 1.2" />
              </svg>
              <div className="relative w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden bg-[#1F2A4A]">
                <img
                  src="/sa/benoit-sellerie-academie-photoroom-1.png"
                  alt="Benki de Gamenki"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="font-light text-2xl md:text-3xl text-[#1F2A4A]">
                Encadré par <span className="font-serif italic text-[#C9B896]">Benki de Gamenki</span>,
              </h3>
              <p className="text-[#4A5568] mt-2 leading-relaxed max-w-xl">
                maître sellier-garnisseur formé chez les Compagnons du Devoir, 20+ ans de métier.
              </p>
              <Link to={withLangPrefix('/a-propos', lang)} className="group inline-flex items-baseline gap-3 mt-4 text-[#1F2A4A] border-b border-[#C9B896] pb-0.5 hover:gap-5 transition-all duration-500">
                <span className="text-xs uppercase tracking-[0.25em]">Lire son parcours</span>
                <span className="text-[#C9B896]">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── 08 · E-LEARNING TEASER (DARK) ───────── */}
      <section className="bg-[#172239] py-14 md:py-20 relative overflow-hidden" data-testid="elearning-section">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-12 gap-6 items-end">
            <div className="col-span-12 md:col-span-7">
              <h2 className="font-light tracking-tight text-white text-2xl md:text-4xl lg:text-5xl leading-[1.05]">
                L'académie en ligne,<br />
                <span className="font-serif italic font-normal text-[#C9B896]">à ton rythme.</span>
              </h2>
              <p className="text-white/65 text-lg leading-relaxed font-light max-w-xl mt-8">
                Une plateforme vidéo en préparation. Premier épisode gratuit par formation, modules suivants débloqués progressivement. Inscris-toi pour être prévenu·e du lancement.
              </p>

              <form onSubmit={handleTeaser} className="flex items-end gap-6 mt-12 max-w-md" data-testid="elearning-form">
                <div className="flex-1 relative">
                  <label className="block text-[10px] uppercase tracking-[0.3em] text-[#C9B896]/85 mb-2">Adresse email</label>
                  <input
                    type="email"
                    value={teaserEmail}
                    onChange={(e) => setTeaserEmail(e.target.value)}
                    placeholder="ton@email.com"
                    required
                    className="w-full bg-transparent border-0 border-b border-[#C9B896]/40 focus:border-[#C9B896] focus:outline-none text-white py-2 placeholder:text-white/30"
                    data-testid="elearning-email-input"
                  />
                </div>
                <button
                  type="submit"
                  className="group inline-flex items-baseline gap-2 text-[#C9B896] border-b border-[#C9B896] pb-1.5 hover:gap-4 transition-all duration-500 text-sm uppercase tracking-[0.25em] whitespace-nowrap"
                  data-testid="elearning-submit"
                >
                  M'avertir <span>→</span>
                </button>
              </form>
            </div>

            <div className="col-span-12 md:col-span-4 md:col-start-9 hidden md:block">
              <div className="space-y-5 text-right">
                {[
                  { num: '100+', label: 'épisodes prévus' },
                  { num: '7', label: 'parcours' },
                  { num: '24/7', label: 'accès illimité' },
                ].map((s, i) => (
                  <div key={i} className="border-t border-[#C9B896]/25 pt-3">
                    <p className="text-3xl md:text-4xl font-light text-[#C9B896]">{s.num}</p>
                    <p className="text-xs uppercase tracking-[0.2em] text-white/55 mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── 09 · TRUST + FINAL CTA ───────── */}
      <section className="py-14 md:py-20 relative" data-testid="final-cta">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          {/* Urgency banner */}
          <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-[#1F2A4A] text-white mb-10">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#C9B896] opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#C9B896]" />
            </span>
            <span className="text-xs uppercase tracking-[0.25em] font-medium">Sessions en cours d'ouverture · places limitées</span>
          </div>

          <h2 className="font-light tracking-tight text-[#1F2A4A] text-3xl md:text-5xl lg:text-6xl leading-[1.05] mb-8">
            <span className="block">Ne passe pas</span>
            <span className="block font-serif italic font-medium text-[#C9B896]">à côté de ta vie.</span>
          </h2>

          <p className="text-[#4A5568] text-lg leading-relaxed font-light max-w-2xl mx-auto mb-10">
            Le métier de sellier se vit, il ne s'attend pas. Dépose ton dossier en 5 minutes — l'école te recontacte personnellement sous 48 heures pour valider ton projet.
          </p>

          {/* Qualiopi inline pill — hidden on home at client request (visible on
              formation pages and footer only). */}
          {false && (
          <div className="inline-flex items-center gap-5 px-7 py-4 rounded-full border border-[#C9B896]/40 mb-8 bg-white">
            <QualiopiBadge size="md" />
            <div className="text-left border-l border-[#1F2A4A]/10 pl-5">
              <p className="text-xs font-semibold text-[#1F2A4A] uppercase tracking-[0.2em]">Certifié Qualiopi</p>
              <p className="text-[11px] text-[#4A5568]">Plusieurs solutions de financement disponibles</p>
            </div>
          </div>
          )}

          {/* Partenaires de financement — temporarily hidden at client's request.
              Kept commented for easy reactivation later. */}
          {false && (
          <div className="mb-12" data-testid="home-funding-partners">
            <p className="text-[10px] font-mono uppercase tracking-[0.35em] text-[#4A5568]/55 mb-5">
              Acteurs de financement reconnus
            </p>
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12 max-w-3xl mx-auto">
              <a
                href="https://www.francetravail.fr/accueil/"
                target="_blank"
                rel="noopener noreferrer"
                title="France Travail — financement AIF des demandeurs d'emploi"
                className="opacity-70 hover:opacity-100 transition-opacity"
                data-testid="partner-link-france-travail"
              >
                <img src="/financeurs/france-travail.png" alt="France Travail" className="h-12 w-auto object-contain" loading="lazy" />
              </a>
              <a
                href="https://www.fafcea.com/"
                target="_blank"
                rel="noopener noreferrer"
                title="FAFCEA — fonds de formation des artisans"
                className="opacity-70 hover:opacity-100 transition-opacity"
                data-testid="partner-link-fafcea"
              >
                <img src="/financeurs/fafcea.svg" alt="FAFCEA" className="h-12 w-auto object-contain" loading="lazy" />
              </a>
              <a
                href="https://www.agefiph.fr/"
                target="_blank"
                rel="noopener noreferrer"
                title="AGEFIPH — financement pour les personnes en situation de handicap"
                className="opacity-70 hover:opacity-100 transition-opacity"
                data-testid="partner-link-agefiph"
              >
                <img src="/financeurs/agefiph.svg" alt="AGEFIPH" className="h-12 w-auto object-contain" loading="lazy" />
              </a>
            </div>
            <Link
              to={withLangPrefix('/financements', lang)}
              className="inline-block mt-5 text-[11px] uppercase tracking-[0.25em] text-[#1F2A4A] hover:text-[#C9B896] border-b border-[#C9B896]/40 pb-0.5 transition-colors"
              data-testid="home-funding-link"
            >
              Voir tous les financements →
            </Link>
          </div>
          )}

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <Link to={withLangPrefix('/formations-sur-place', lang)}>
              <Button className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] rounded-full px-10 py-7 text-sm uppercase tracking-[0.25em] font-semibold shadow-2xl shadow-[#C9B896]/40" data-testid="final-cta-formations">
                Déposer ma candidature
              </Button>
            </Link>
            <Link to={withLangPrefix('/contact', lang)} className="group inline-flex items-baseline gap-3 text-[#1F2A4A] border-b border-[#C9B896] pb-1 hover:gap-5 transition-all duration-500">
              <span className="text-sm uppercase tracking-[0.25em]">Échanger avec l'école</span>
              <span className="text-[#C9B896]">→</span>
            </Link>
          </div>

          <p className="text-xs text-[#4A5568]/60 mt-10 font-mono tracking-wider">
            ⏱ Réponse personnalisée sous 48 h · Sans engagement
          </p>
        </div>
      </section>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// ArtisanNetworkCTA — rotating photo carousel + STS network lead capture.
// Section shown on the home page below the video testimonials. Sends
// visitors to `/artisan-pres-de-chez-vous` where they submit a lead that
// the STS network dispatches to a qualified professional (48 h response).
// ─────────────────────────────────────────────────────────────────────
const ArtisanNetworkCTA = ({ lang }) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % ARTISAN_ROTATING_IMAGES.length), 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      className="py-16 md:py-24 relative overflow-hidden bg-[#1F2A4A] text-[#FAF7F0]"
      data-testid="home-artisan-cta"
    >
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />

      <div className="relative max-w-6xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
          {/* Copy */}
          <div className="md:col-span-7 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9B896] mb-3 font-semibold">
              Réseau STS · Sellerie Tout Simplement
            </p>
            <h2 className="font-light text-3xl md:text-5xl leading-[1.1] mb-5 text-[#FAF7F0]">
              Vous cherchez un <span className="font-serif italic text-[#C9B896]">artisan qualifié</span>
              <br /> près de chez vous&nbsp;?
            </h2>
            <p className="text-[#E8DCC8]/90 font-light text-base md:text-lg leading-relaxed mb-6 max-w-xl">
              Déposez ici votre demande, elle sera <b className="text-[#FAF7F0]">transmise au réseau STS</b> et un professionnel vous recontacte <b className="text-[#FAF7F0]">sous 48 h</b>.
            </p>
            <ul className="space-y-2 mb-8 text-sm text-[#E8DCC8]/85 font-light">
              <li className="flex items-start gap-2.5"><span className="text-[#C9B896] mt-1.5">▸</span> Artisans formés par la Sellerie Académie</li>
              <li className="flex items-start gap-2.5"><span className="text-[#C9B896] mt-1.5">▸</span> Dispatch géographique automatique</li>
              <li className="flex items-start gap-2.5"><span className="text-[#C9B896] mt-1.5">▸</span> Aucun engagement — service gratuit</li>
            </ul>
            <Link
              to={withLangPrefix('/artisan-pres-de-chez-vous', lang)}
              className="inline-flex items-center gap-2 bg-[#C9B896] hover:bg-[#E0D4BA] text-[#1F2A4A] font-semibold rounded-full px-8 h-12 text-sm uppercase tracking-[0.15em] transition-colors shadow-lg shadow-[#C9B896]/25"
              data-testid="home-artisan-cta-btn"
            >
              Déposer ma demande →
            </Link>
          </div>

          {/* Rotating photos */}
          <div className="md:col-span-5 min-w-0 hidden md:block">
            <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-[#C9B896]/25 shadow-2xl shadow-black/30">
              {ARTISAN_ROTATING_IMAGES.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={`Réalisation Sellerie Académie ${i + 1}`}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === idx ? 'opacity-100' : 'opacity-0'}`}
                  loading="lazy"
                />
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-[#1F2A4A] via-[#1F2A4A]/20 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9B896] font-semibold mb-1">Réseau national</p>
                <p className="text-sm text-[#FAF7F0] font-light leading-tight">
                  Des artisans certifiés partout en France.
                </p>
              </div>
              {/* Dots */}
              <div className="absolute bottom-2 right-3 flex gap-1">
                {ARTISAN_ROTATING_IMAGES.map((_, i) => (
                  <span key={i} className={`h-1 rounded-full transition-all ${i === idx ? 'w-4 bg-[#C9B896]' : 'w-1 bg-white/35'}`} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

